import { SLOTS_APIS } from "@/api_framework/api_config";

import { useAppDispatch } from "@/app_redux/hooks/root_hook";

import axios, { AxiosError, AxiosResponse } from "axios";
import { useCallback } from "react";
import toast from "react-hot-toast";

import {
  IAtHomeSlotBucket,
  IAtHomeSlotBucketResponse,
  IBookingSlotsDoctorTag,
  IBookingSlotsFilterBody,
  IBookingSlotsResponse,
  IBookingSlotsResponseData,
} from "@/api_framework/api_modals/Bookings";
import { setLoading } from "@/app_redux/reducers/loading_reducer";
import { setAtHomeSlotBuckets, setSelectedSlotDetails, setSlotsList } from "@/app_redux/reducers/slices/auth/slots_slice";
import AxiosCurlirize from "axios-curlirize";
import { useConfiguration } from "../../useConfiguration";
import { useCurlLogger } from "../../useCurlLogger";
import { HOME_SERVICE_ID } from "@/constants";

AxiosCurlirize(axios, () => {});


interface IAtHomeAvailableSlotData {
  slot_id: string;
  start_time: string;
  end_time: string;
  prepaid_booking_slot?: string;
  booking_fee?: number;
  configuration_id?: string | null;
}

interface IAtHomeAvailableStaffData {
  staff_id: string;
  staff_name: string;
  experience?: string;
  expertise?: string;
  last_visited_staff?: boolean | null;
  preferred_staff?: boolean | null;
  tags?: IBookingSlotsDoctorTag[];
  slots?: IAtHomeAvailableSlotData[];
}

interface IAtHomeAvailableClinicData {
  clinic_id: string;
  clinic_name: string;
  user_persona_message?: string | null;
  staff?: IAtHomeAvailableStaffData[];
}

const parseCoordinate = (value?: string): number | null => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const buildAtHomeAvailablePayload = (filterParams: IBookingSlotsFilterBody) => {
  return {
    clinic_ids: filterParams?.clinic_ids ?? [],
    staff_ids: filterParams?.staff_ids ?? [],
    date: filterParams?.booking_date,
    service_type_id: filterParams?.service_type_id,
    service_id: filterParams?.service_id,
    sub_service_id: filterParams?.sub_service_id,
    pet_type_id: filterParams?.pet_species_id,
    customer_id: filterParams?.user_id,
    booking_id: filterParams?.booking_id,
    latitude: parseCoordinate(filterParams?.location_details?.latitude),
    longitude: parseCoordinate(filterParams?.location_details?.longitude),
    ...(filterParams?.previous_flow ? { previous_flow: true } : {}),
  };
};

const normalizeCityIdForPayload = (cityId: IBookingSlotsFilterBody["city_id"]): string | null => {
  if (Array.isArray(cityId)) return cityId[0] ? String(cityId[0]) : null;
  if (typeof cityId === "string") return cityId || null;
  return null;
};

const normalizeAtHomeAvailableSlotsResponseData = (
  data: IAtHomeAvailableClinicData[] | null | undefined,
): IBookingSlotsResponseData[] => {
  return (data ?? []).map((clinic) => ({
    clinic_id: clinic?.clinic_id,
    clinic_name: clinic?.clinic_name,
    vetbuddy_clinic_name: clinic?.clinic_name,
    user_persona_message: clinic?.user_persona_message ?? null,
    distance_from_user: "",
    data: (clinic?.staff ?? []).map((staff) => ({
      staff_id: staff?.staff_id,
      staff_name: staff?.staff_name,
      experience: staff?.experience ?? "",
      expertise: staff?.expertise ?? "",
      last_visited_staff: staff?.last_visited_staff ?? false,
      preferred_staff: staff?.preferred_staff ?? false,
      tags: staff?.tags ?? [],
      data: (staff?.slots ?? []).map((slot) => ({
        slot_id: slot?.slot_id,
        start_time: slot?.start_time,
        end_time: slot?.end_time,
        prepaid_booking_slot: slot?.prepaid_booking_slot ?? "NO",
        booking_fee: slot?.booking_fee,
        configuration_id: slot?.configuration_id ?? "",
      })),
    })),
  }));
};

const normalizeAtHomeSlotBuckets = (
  buckets: IAtHomeSlotBucketResponse[] | null | undefined,
): IAtHomeSlotBucket[] => {
  return (buckets ?? [])
    .filter((bucket) => bucket?.slots?.length > 0)
    .map((bucket) => ({
      heading: bucket?.heading,
      slots: bucket?.slots?.map((slot) => ({
        slot_id: slot?.id,
        staff_id: slot?.staff_id,
        clinic_id: slot?.clinic_id,
        start_time: slot?.start_time,
        end_time: slot?.end_time,
        prepaid_booking_slot: slot?.is_prepaid_slot ? "YES" : "NO",
        booking_fee: slot?.booking_fee ?? undefined,
        configuration_id: slot?.configuration_id ?? "",
      })),
    }));
};

export const useBookingSlotsListAPI = () => {
  const dispatch = useAppDispatch();
  const configuration = useConfiguration();
  const { sendCurl } = useCurlLogger();

  const execute = useCallback(
    async (filterParams: IBookingSlotsFilterBody) => {
      dispatch(
        setLoading({
          key: "isAvailableSlotsInProgress",
          status: true,
        }),
      );
      dispatch(
        setSelectedSlotDetails({
          data: null,
        }),
      );
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        const isAtHomeService = filterParams?.service_type_id === HOME_SERVICE_ID;
        /* booking_id is only sent while rescheduling, so a fresh AHS booking is the bucketed response */
        const isAtHomeBucketResponse =
          isAtHomeService && !filterParams?.booking_id && !filterParams?.previous_flow;
        const endpoint = isAtHomeService ? SLOTS_APIS?.AT_HOME_SLOTS_LIST?.baseURL : SLOTS_APIS?.SLOTS_LIST?.baseURL;
        const payload = isAtHomeService
          ? buildAtHomeAvailablePayload(filterParams)
          : { ...filterParams, city_id: normalizeCityIdForPayload(filterParams?.city_id) };
        const response = await axios

          .post(endpoint ?? "", payload, {
            headers: {
              Authorization: `${accessToken}`,
              "Content-Type": "application/json",
            },
          })
          .then((res: AxiosResponse<IBookingSlotsResponse>) => {
            dispatch(
              setLoading({
                key: "isAvailableSlotsInProgress",
                status: false,
              }),
            );
            const atHomeSlotBuckets = isAtHomeBucketResponse
              ? normalizeAtHomeSlotBuckets((res.data as any)?.data?.slot_buckets)
              : [];
            let normalizedData: IBookingSlotsResponseData[] = res.data?.data ?? [];
            if (isAtHomeBucketResponse) {
              normalizedData = [];
            } else if (isAtHomeService) {
              normalizedData = normalizeAtHomeAvailableSlotsResponseData((res.data as any)?.data);
            }
            if (res.data.status === true) {
              dispatch(
                setSlotsList({
                  data: normalizedData,
                }),
              );
              dispatch(
                setAtHomeSlotBuckets({
                  data: atHomeSlotBuckets,
                }),
              );
              return { status: true, message: normalizedData };
            } else {
              dispatch(
                setSlotsList({
                  data: [],
                }),
              );
              dispatch(
                setAtHomeSlotBuckets({
                  data: [],
                }),
              );
              return { status: false, message: null };
            }
          })
          .catch((e: AxiosError) => {
            // captureError(e);
            sendCurl(e.config["curlCommand"]);
            dispatch(
              setLoading({
                key: "isAvailableSlotsInProgress",
                status: false,
              }),
            );
            dispatch(
              setSlotsList({
                data: [],
              }),
            );
            dispatch(
              setAtHomeSlotBuckets({
                data: [],
              }),
            );
            return { status: false, message: null };
          });
        return response;
      } catch (e: any) {
        toast.error("Server Error: " + e.message);
      }
    },
    [dispatch, configuration, sendCurl],
  );
  return execute;
};
